// frontend/src/components/common/Tooltip.jsx
// Hybrid Tooltip Component with AVA Integration
// =============================================
// Shows instant static tooltip + optional "Ask AVA" for deeper help
// Mobile-friendly with tap-to-show behavior
// Created: Dec 31, 2025 (Task 4.9)

import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, MessageCircle, X, ChevronRight } from 'lucide-react';

/**
 * Hybrid Tooltip Component
 * 
 * @param {string} content - The static tooltip text (required)
 * @param {string} avaPrompt - Pre-filled question for AVA (optional)
 * @param {string} avaContext - Additional context for AVA (optional)
 * @param {React.ReactNode} children - The trigger element (optional, defaults to ? icon)
 * @param {string} position - Tooltip position: 'top' | 'bottom' | 'left' | 'right' (default: 'top')
 * @param {string} size - Icon size: 'sm' | 'md' | 'lg' (default: 'sm')
 * @param {string} variant - Style variant: 'default' | 'info' | 'warning' | 'success' (default: 'default')
 * @param {number} maxWidth - Max width in pixels (default: 280)
 * @param {boolean} showAvaLink - Whether to show "Ask AVA" link (default: true if avaPrompt provided)
 */
const Tooltip = ({
  content,
  avaPrompt,
  avaContext,
  children,
  position = 'top',
  size = 'sm',
  variant = 'default',
  maxWidth = 280,
  showAvaLink = true,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close on outside click (mobile)
  useEffect(() => {
    if (isVisible && isMobile) {
      const handleClickOutside = (e) => {
        if (tooltipRef.current && !tooltipRef.current.contains(e.target) &&
            triggerRef.current && !triggerRef.current.contains(e.target)) {
          setIsVisible(false);
        }
      };
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('touchstart', handleClickOutside);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isVisible, isMobile]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (!isMobile) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      timeoutRef.current = setTimeout(() => setIsVisible(false), 150);
    }
  };

  const handleClick = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setIsVisible(!isVisible);
    }
  };

  const handleAskAva = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVisible(false);

    // Dispatch event to open AVA with the prompt
    const avaEvent = new CustomEvent('ava-open', {
      detail: {
        prompt: avaPrompt,
        context: avaContext,
        autoSend: true
      }
    });
    window.dispatchEvent(avaEvent);
  };

  // Size classes for the trigger icon
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  // Variant colors
  const variantClasses = {
    default: 'text-gray-400 hover:text-gray-600',
    info: 'text-blue-400 hover:text-blue-600',
    warning: 'text-yellow-500 hover:text-yellow-600',
    success: 'text-green-400 hover:text-green-600'
  };

  // Tooltip background variants
  const tooltipBgClasses = {
    default: 'bg-gray-900',
    info: 'bg-blue-900',
    warning: 'bg-yellow-900',
    success: 'bg-green-900'
  };

  // Position classes
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  // Arrow position classes
  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 border-l-transparent border-r-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 border-l-transparent border-r-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-900 border-t-transparent border-b-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-900 border-t-transparent border-b-transparent border-l-transparent'
  };

  const shouldShowAvaLink = showAvaLink && avaPrompt;

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`${variantClasses[variant]} transition-colors cursor-help focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full`}
        aria-label="Help"
        aria-describedby={isVisible ? 'tooltip-content' : undefined}
      >
        {children || <HelpCircle className={sizeClasses[size]} />}
      </button>

      {/* Tooltip */}
      {isVisible && (
        <div
          ref={tooltipRef}
          id="tooltip-content"
          role="tooltip"
          className={`absolute z-50 ${positionClasses[position]}`}
          style={{ maxWidth: `${maxWidth}px`, minWidth: '200px' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className={`${tooltipBgClasses[variant]} text-white rounded-lg shadow-xl`}>
            {/* Close button for mobile */}
            {isMobile && (
              <button
                onClick={() => setIsVisible(false)}
                className="absolute top-2 right-2 text-gray-400 hover:text-white"
                aria-label="Close tooltip"
              >
                <X size={14} />
              </button>
            )}

            {/* Content */}
            <div className={`p-3 text-sm leading-relaxed ${isMobile ? 'pr-8' : ''}`}>
              {content}
            </div>

            {/* Ask AVA Link */}
            {shouldShowAvaLink && (
              <button
                onClick={handleAskAva}
                className="w-full flex items-center justify-between px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-b-lg transition-colors"
              >
                <span className="flex items-center gap-2">
                  <MessageCircle size={14} />
                  Ask AVA for more help
                </span>
                <ChevronRight size={14} />
              </button>
            )}
          </div>

          {/* Arrow */}
          <div
            className={`absolute w-0 h-0 border-[6px] ${arrowClasses[position]}`}
            style={{ 
              borderTopColor: position === 'top' ? (variant === 'default' ? '#111827' : '') : 'transparent',
              borderBottomColor: position === 'bottom' ? (variant === 'default' ? '#111827' : '') : 'transparent'
            }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Pre-configured tooltip variants for common use cases
 */

// Trust Score Tooltip
export const TrustScoreTooltip = ({ score, className }) => (
  <Tooltip
    content={`Your trust score (${score}/5) reflects your reliability. Complete jobs, pay on time, and get good reviews to build trust in the community.`}
    avaPrompt="How can I improve my trust score?"
    avaContext="user asking about trust score improvement"
    position="top"
    variant="default"
    className={className}
  />
);

// Token Balance Tooltip
export const TokenBalanceTooltip = ({ balance, className }) => (
  <Tooltip
    content={`You have ${balance} tokens worth $${(balance * 0.10).toFixed(2)}. Use tokens at checkout for instant discounts! Earn more through purchases, referrals, and completing jobs.`}
    avaPrompt="How do I earn more tokens?"
    avaContext="user asking about earning tokens"
    position="bottom"
    variant="info"
    className={className}
  />
);

// Loyalty Tier Tooltip
export const LoyaltyTierTooltip = ({ tier, className }) => {
  const tierInfo = {
    'NEW_USER': { cap: '$15', next: 'Bronze (5 purchases)' },
    'BRONZE': { cap: '$25', next: 'Silver (15 purchases + 2 referrals)' },
    'SILVER': { cap: '$40', next: 'Gold (30 purchases + 5 referrals)' },
    'GOLD': { cap: '$75', next: 'Platinum (50 purchases + 10 referrals)' },
    'PLATINUM': { cap: 'Unlimited', next: null }
  };
  const info = tierInfo[tier] || tierInfo['NEW_USER'];
  
  return (
    <Tooltip
      content={`Your ${tier.replace('_', ' ')} tier allows up to ${info.cap} token redemption per order. ${info.next ? `Next: ${info.next}` : 'You\'ve reached the highest tier!'}`}
      avaPrompt="How do loyalty tiers work?"
      avaContext="user asking about loyalty tier system"
      position="top"
      variant="info"
      className={className}
    />
  );
};

// Redemption Cap Tooltip
export const RedemptionCapTooltip = ({ className }) => (
  <Tooltip
    content="HYBRID system: Redeem up to 40% of your cart total OR your tier's dollar cap — whichever is less. Higher tiers unlock bigger redemptions!"
    avaPrompt="Explain the token redemption limits"
    avaContext="user asking about HYBRID redemption caps"
    position="top"
    variant="warning"
    className={className}
  />
);

// Savings Amount Tooltip
export const SavingsTooltip = ({ amount, className }) => (
  <Tooltip
    content={`You've saved $${amount.toFixed(2)} compared to retail prices! Group buying unlocks wholesale rates that stores can't match.`}
    avaPrompt="How does UniLedge calculate my savings?"
    avaContext="user asking about savings calculation"
    position="top"
    variant="success"
    className={className}
  />
);

// Referral Bonus Tooltip
export const ReferralTooltip = ({ className }) => (
  <Tooltip
    content="Earn 25 tokens instantly when a friend joins + 10% of their token earnings for 90 days! Your friend also gets 25 bonus tokens."
    avaPrompt="Tell me more about the referral program"
    avaContext="user asking about referral rewards"
    position="top"
    variant="info"
    className={className}
  />
);

// Pickup Bonus Tooltip
export const PickupBonusTooltip = ({ bonus, className }) => (
  <Tooltip
    content={`Earn ${bonus} extra tokens by choosing pickup! This helps reduce logistics costs and supports community hubs. Plus, you save on delivery fees.`}
    avaPrompt="Why does pickup earn bonus tokens?"
    avaContext="user asking about pickup bonus"
    position="top"
    variant="success"
    className={className}
  />
);

// Group Buying Tooltip
export const GroupBuyingTooltip = ({ className }) => (
  <Tooltip
    content="When more people buy together, everyone gets a better price! Watch the member count grow to unlock deeper discounts."
    avaPrompt="How does group buying work on UniLedge?"
    avaContext="user asking about group buying"
    position="top"
    variant="info"
    className={className}
  />
);

// Price Tier Tooltip
export const PriceTierTooltip = ({ currentTier, nextTier, buyersNeeded, className }) => (
  <Tooltip
    content={`Currently at ${currentTier} pricing. ${buyersNeeded > 0 ? `${buyersNeeded} more buyers unlocks ${nextTier} pricing!` : 'Maximum discount unlocked!'}`}
    avaPrompt="How do price tiers work?"
    avaContext="user asking about price tiers and discounts"
    position="top"
    variant="info"
    className={className}
  />
);

// Job Tokens Tooltip
export const JobTokensTooltip = ({ tokenAmount, className }) => (
  <Tooltip
    content={`This job pays ${tokenAmount} tokens ($${(tokenAmount * 0.10).toFixed(2)} value). Complete it successfully to earn tokens and boost your trust score!`}
    avaPrompt="How do I earn tokens from jobs?"
    avaContext="user asking about job rewards"
    position="top"
    variant="success"
    className={className}
  />
);

// Milestone Tooltip
export const MilestoneTooltip = ({ milestoneName, reward, className }) => (
  <Tooltip
    content={`"${milestoneName}" awards ${reward} tokens when completed. Check your progress in the Achievements tab!`}
    avaPrompt="What milestones can I complete?"
    avaContext="user asking about milestone achievements"
    position="top"
    variant="info"
    className={className}
  />
);

// Token Expiration Tooltip
export const TokenExpirationTooltip = ({ daysLeft, tier, className }) => (
  <Tooltip
    content={`Your tokens expire in ${daysLeft} days if unused. Stay active by making purchases or completing jobs to reset the timer! Higher tiers get longer expiration.`}
    avaPrompt="How does token expiration work?"
    avaContext="user asking about token expiration"
    position="top"
    variant="warning"
    className={className}
  />
);

export default Tooltip;